<template>
  <Banners>
    <Header
      :broker="broker"
      :assessment-result="overallResult"
      :loading="loading"
      :disabled="calculateRemainingProfileViews < 1"
    />
    <div class="relative">
      <div v-if="!hasEldConnected">
        <div
          :class="
            calculateRemainingProfileViews < 1 &&
            'blur-backgound absolute z-20 w-full'
          "
        />
        <div
          :class="
            calculateRemainingProfileViews < 1 &&
            'white-backdrop absolute z-20 h-full w-full bg-white'
          "
        />
      </div>
      <div
        v-if="
          !hasEldConnected && showPaywallBanner && calculateProfileViewRange
        "
        class="flex h-full w-full flex-col justify-between gap-6 border border-yellow-500 bg-yellow-10 p-2"
      >
        <p class="text-yellow-500">
          You have {{ calculateRemainingProfileViews }} more profile view{{
            calculateRemainingProfileViews > 1 && 's'
          }}
          remaining.
          <span
            class="cursor-pointer font-bold underline"
            @click="navigateEld"
          >
            Connect your ELD
          </span>
          to get full access.
        </p>
        <div
          class="absolute right-1 flex h-6 w-6 cursor-pointer items-center justify-center p-1 md:right-4"
          @click="dismissBanner"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="11"
            viewBox="0 0 10 11"
            fill="none"
          >
            <path
              d="M1 9.5L9 1.5"
              stroke="#f8c617"
              stroke-width="2"
            />
            <path
              d="M1 1.5L9 9.5"
              stroke="#f8c617"
              stroke-width="2"
            />
          </svg>
        </div>
      </div>
      <div
        class="container-max mx-auto flex w-full flex-col gap-4 px-4 py-6 md:px-10"
      >
        <Tabs
          v-model="selectedTab"
          :tabs="['Overview']"
        />
      </div>
      <template v-if="!loading">
        <section
          v-if="selectedTab === 'Overview'"
          class="container-max mx-auto grid w-full grid-cols-1 gap-4 px-4 md:px-10 lg:grid-cols-7"
        >
          <div class="toc-section col-span-1 hidden lg:block">
            <table-of-contents
              ref="tableOfContents"
              class="relative"
              :show-insights="insights.length > 0"
            />
          </div>
          <div
            id="assessment"
            class="toc-section col-span-6"
          >
            <div class="mb-10 grid">
              <assessment-section
                :assessment="broker?.broker_rules"
                :overall-result="overallResult"
              />
            </div>
            <div
              v-if="insights.length > 0"
              id="insights"
              class="toc-section mb-10"
            >
              <insights-section
                :insights="insights"
                class="mb-5"
                :screen-width="screenWidth"
              />
              <insights-details
                :insights="insights"
                :screen-width="screenWidth"
              />
            </div>
            <div
              id="endorsements"
              class="toc-section mb-10 grid"
            >
              <Endorsements
                :credit-approval="creditStatus"
                :affiliations="broker?.broker_affiliations"
                :size="broker?.broker_size"
              />
            </div>
            <div
              id="secure-interactions"
              class="toc-section mb-10 grid"
            >
              <div class="md:col-span-1 lg:col-span-2">
                <secure-interactions
                  :rate-con-information="broker?.rate_con_contacts"
                  :invoicing-contacts="broker?.invoicing_contact"
                  :digital-activities="broker?.digital_activities"
                />
              </div>
            </div>
            <div
              id="authority"
              class="toc-section my-10 lg:grid"
            >
              <Authority :authority="broker?.authority" />
            </div>
            <div class="mb-5 flex flex-col gap-2 text-left text-body-color">
              <p class="text-3xs">
                * Gross revenue is provided for informational purposes only.
                While efforts have been made to ensure accuracy, Highway makes
                no claims or guarantees about the validity of the information
                and encourages all users to verify facts independently. Please
                contact Highway at
                <a
                  class="underline"
                  href="mailto:service@highway.com"
                  >service@highway.com</a
                >
                if you believe this information may be incorrect.
              </p>
              <p class="text-3xs">
                ** Approval for credit with Triumph is provided as a snapshot in
                time. It is not a guarantee that such broker is solvent nor is
                it an affirmative or implied representation by Triumph of the
                broker’s creditworthiness nor should it be relied upon as such.
              </p>
            </div>
          </div>
        </section>
      </template>
      <template v-else>
        <section
          v-if="selectedTab === 'Overview'"
          class="container-max mx-auto grid w-full grid-cols-1 gap-4 px-4 md:px-10 lg:grid-cols-7"
        >
          <div class="toc-section col-span-1 -mt-6 hidden lg:block">
            <table-of-contents
              :loading="true"
              class="relative"
            />
          </div>
          <div
            id="assessment"
            class="toc-section col-span-6"
          >
            <div class="mb-5 grid">
              <div class="force-inline-flex loading-gradient rounded-md">
                <div
                  class="block w-full rounded-md border border-lighter-gray px-3 py-10"
                >
                  <h3 class="text-left text-xl text-light-gray">Assessment</h3>
                </div>
              </div>
            </div>
            <div
              id="insights"
              class="toc-section mb-5"
            >
              <div class="force-inline-flex loading-gradient rounded-md">
                <div
                  class="block w-full rounded-md border border-lighter-gray px-3 py-10"
                >
                  <h3 class="text-left text-xl text-light-gray">Insights</h3>
                </div>
              </div>
            </div>
            <div
              id="endorsements"
              class="toc-section mb-5 grid"
            >
              <div class="force-inline-flex loading-gradient rounded-md">
                <div
                  class="block w-full rounded-md border border-lighter-gray px-3 py-10"
                >
                  <h3 class="text-left text-xl text-light-gray">
                    Endorsements
                  </h3>
                </div>
              </div>
            </div>
            <div
              id="endorsements"
              class="toc-section mb-5 grid"
            >
              <div class="force-inline-flex loading-gradient rounded-md">
                <div
                  class="block w-full rounded-md border border-lighter-gray px-3 py-10"
                >
                  <h3 class="text-left text-xl text-light-gray">
                    Secure Communications
                  </h3>
                </div>
              </div>
            </div>
            <div
              id="endorsements"
              class="toc-section mb-5 grid"
            >
              <div class="force-inline-flex loading-gradient rounded-md">
                <div
                  class="block w-full rounded-md border border-lighter-gray p-10"
                >
                  <h3 class="text-left text-xl text-light-gray">Authority</h3>
                </div>
              </div>
            </div>
          </div>
        </section>
      </template>

      <button-main
        v-if="!hasEldConnected && calculateRemainingProfileViews < 1"
        variant="secondary"
        class="absolute left-0 right-0 top-40 z-30 flex items-center gap-2"
        style="margin-inline: auto; width: max-content"
        @click="togglePaywallModal"
      >
        View more Broker profiles
        <img
          class="relative z-10"
          src="@/assets/plus-gray.svg"
        />
      </button-main>

      <connect-eld-modal
        v-if="!hasEldConnected && showPaywallModal"
        :visible="showPaywallModal"
        @close="closeModal"
        @connect-eld="navigateEld"
      />
      <dialog
        id="dialog"
        class="outline-none"
      >
        <carrier-portal-eld-provider-modal />
      </dialog>
    </div>
  </Banners>
  <Footer :no-absolute="true" />
</template>
<script>
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { ref, onBeforeMount, onMounted, onBeforeUnmount, computed } from 'vue';
import Tabs from '@/components/show/Tabs.vue';
import Banners from '@/components/Banners.vue';
import Footer from '@/components/Footer.vue';
import Header from '@/components/show/Header.vue';
import Authority from '@/components/show/Authority.vue';
import Endorsements from '@/components/show/Endorsements.vue';
import TableOfContents from '@/components/TableOfContents.vue';
import InsightsSection from '@/components/InsightsSection.vue';
import SecureInteractions from '@/components/show/SecureInteractions.vue';
import AssessmentSection from '@/components/AssessmentSection.vue';
import InsightsDetails from '@/components/Insights/InsightsDetails.vue';
import CarrierPortalEldProviderModal from '@/components/modals/CarrierPortalEldProviderModal.vue';
import ConnectEldModal from '@/components/modals/ConnectEldModal.vue';
import ButtonMain from '@/components/Button.vue';

export default {
  components: {
    Tabs,
    Header,
    Banners,
    Footer,
    Authority,
    Endorsements,
    InsightsSection,
    InsightsDetails,
    TableOfContents,
    AssessmentSection,
    SecureInteractions,
    CarrierPortalEldProviderModal,
    ConnectEldModal,
    ButtonMain,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const selectedTab = ref('Overview');
    const loading = ref(false);
    const showPaywallBanner = ref(true);
    const showPaywallModal = ref(false);

    onBeforeMount(async () => {
      loading.value = true;
      try {
        await Promise.all([
          store.dispatch(
            'show/getBrokerDetail',
            router.currentRoute.value.params.id
          ),
          store.dispatch('brokers/getBrokers', {}),
          store.dispatch(
            'carriers/getCarriersInspections',
            router.currentRoute.value.params.id
          ),
          store.dispatch(
            'carriers/getCarriersSafety',
            router.currentRoute.value.params.id
          ),
        ]);
      } finally {
        loading.value = false;
        store.commit('carriers/setShowCarrierInsights', insights.value);
      }
    });
    onMounted(() => {
      window.addEventListener('resize', updateScreenWidth);
    });
    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateScreenWidth);
    });
    const updateScreenWidth = () => {
      screenWidth.value = window.innerWidth;
    };
    const screenWidth = ref(window.innerWidth);
    const broker = computed(() => store.state.show.showBroker);
    const insights = computed(() => store.state.show.showBroker.insights || []);
    const paywallMetrics = computed(
      () => store.state.show.showBroker.paywall_metrics || 0
    );

    const latestCarrierEldProvider = computed(() => {
      if (store.state.onboardingPacket?.latest_carrier_eld_provider) {
        return store.state.onboardingPacket?.latest_carrier_eld_provider;
      } else if (
        store.state.carriers?.carrierData?.latest_carrier_eld_provider
      ) {
        return store.state.carriers?.carrierData?.latest_carrier_eld_provider;
      }
      return 'null';
    });

    const PAYWALL_VIEW_LIMIT = 10;
    const calculateRemainingProfileViews = computed(
      () => PAYWALL_VIEW_LIMIT - paywallMetrics.value
    );
    const calculateProfileViewRange = computed(
      () =>
        calculateRemainingProfileViews.value > 0 &&
        calculateRemainingProfileViews.value <= 5
    );

    const hasEldConnected =
      latestCarrierEldProvider?.value?.id ||
      latestCarrierEldProvider?.value?.has_setup_manual_connection;

    const dismissBanner = () => (showPaywallBanner.value = false);
    const togglePaywallModal = () => (showPaywallModal.value = true);
    const navigateEld = () => {
      router.push('/carrier?updateEld=true');
    };

    const overallResult = computed(() => {
      const { active_authority, digital_activity_in_usa, credit_approved } =
        broker.value.broker_rules || {};

      if (
        digital_activity_in_usa === 'fail' ||
        credit_approved === 'fail' ||
        active_authority === 'fail'
      ) {
        return 'fail';
      }

      if (
        digital_activity_in_usa === 'incomplete' ||
        credit_approved === 'incomplete'
      ) {
        return 'incomplete';
      }

      if (
        digital_activity_in_usa === 'pass' &&
        credit_approved === 'pass' &&
        active_authority === 'pass'
      ) {
        return 'pass';
      }

      return '';
    });

    const creditStatus = computed(() => {
      if (broker.value?.broker_rules?.credit_approved === 'pass')
        return 'approved';
      return 'unknown';
    });

    return {
      selectedTab,
      screenWidth,
      creditStatus,
      overallResult,
      insights,
      loading,
      broker,
      showPaywallBanner,
      showPaywallModal,
      paywallMetrics,
      latestCarrierEldProvider,
      calculateRemainingProfileViews,
      calculateProfileViewRange,
      hasEldConnected,
      dismissBanner,
      togglePaywallModal,
      navigateEld,
    };
  },
};
</script>

<style scoped>
.grid-rules-for-tpayui {
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr 1fr 1fr;
}

.blur-backgound {
  height: 300px;
  top: 0;
  mask-image: linear-gradient(to top, #fff 40%, transparent 100%);
  backdrop-filter: blur(16px);
}

.white-backdrop {
  top: 300px;
  backdrop-filter: blur(16px);
}
</style>
